import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin } from 'lucide-react';

export function CookiePolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-bold text-sm uppercase tracking-wider">Back to Home</span>
          </button>
          
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-xs font-black text-slate-600 uppercase tracking-widest">Legal Document</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Title Section */}
        <div className="mb-16 text-center">
          <h1 className="text-5xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
            Cookie Policy
          </h1>
          <div className="flex items-center justify-center gap-8 text-sm text-slate-500 font-semibold">
            <div>
              <span className="text-slate-400">Last Updated:</span> January 30, 2026
            </div>
            <div className="w-1 h-1 rounded-full bg-slate-300" />
            <div>
              <span className="text-slate-400">Effective Date:</span> January 30, 2026
            </div>
          </div>
          <div className="mt-4">
            <span className="inline-block px-4 py-2 bg-amber-50 text-amber-700 rounded-full text-xs font-black uppercase tracking-widest">
              QHTECH SOLUTIONS L.L.C
            </span>
          </div>
        </div>

        {/* Content Sections */}
        <div className="prose prose-slate max-w-none">
          
          {/* Section 1 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 text-sm font-black">1</span>
              Introduction to Cookies
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              This Cookie Policy explains how QHTECH SOLUTIONS L.L.C ("Company," "we," "us," or "our") uses cookies and 
              similar tracking technologies on the DAGGPT platform accessible at 
              <a href="https://daggpt.network" className="text-amber-600 hover:text-amber-700 font-semibold"> https://daggpt.network</a> 
              (the "Platform").
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              By continuing to use the Platform, you consent to our use of cookies in accordance with this policy. If you 
              do not agree with our use of cookies, you should adjust your browser settings accordingly or refrain from 
              using the Platform.
            </p>
            <p className="text-slate-600 leading-relaxed">
              This Cookie Policy should be read in conjunction with our Privacy Policy and Terms of Service.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12 bg-amber-50/50 rounded-2xl p-8 border border-amber-100">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-600 text-white text-sm font-black">2</span>
              What Are Cookies?
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Cookies are small text files that are placed on your computer, smartphone, or other device when you visit 
              a website. They are widely used to make websites work more efficiently, provide a better user experience, 
              and deliver reporting information to website owners.
            </p>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">2.1. Types of Cookies</h3>
            <ul className="space-y-3 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Session Cookies:</strong> Temporary cookies that expire when you close 
                  your browser. They enable basic functionality like page navigation and access to secure areas.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Persistent Cookies:</strong> Cookies that remain on your device for a 
                  set period or until you delete them. They remember your preferences and settings across multiple sessions.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">First-Party Cookies:</strong> Cookies set directly by the DAGGPT platform 
                  to enable core functionality and improve your experience.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Third-Party Cookies:</strong> Cookies set by external services we use, 
                  such as analytics providers, payment processors, or content delivery networks.
                </div>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">2.2. Similar Technologies</h3>
            <p className="text-slate-600 leading-relaxed">
              In addition to cookies, we may use similar technologies such as web beacons (pixel tags), local storage, 
              and software development kits (SDKs) to collect information about your use of the Platform.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 text-sm font-black">3</span>
              How We Use Cookies
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              We use cookies for various purposes to enhance your experience on the Platform and improve our services.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.1. Strictly Necessary Cookies</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              These cookies are essential for the Platform to function properly. They enable core features such as:
            </p>
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Authentication:</strong> Keeping you logged into your account</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Security:</strong> Protecting against fraudulent activity and ensuring secure connections</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Session Management:</strong> Maintaining your session state across page loads</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Load Balancing:</strong> Distributing traffic efficiently across our servers</span>
              </li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6 italic text-sm">
              Note: These cookies cannot be disabled as they are essential for the Platform to operate.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.2. Functional Cookies</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              These cookies enable enhanced functionality and personalization:
            </p>
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Preferences:</strong> Remembering your language, theme (light/dark mode), and display settings</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">AI Model Selection:</strong> Saving your preferred AI models for text, image, and video generation</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Generation History:</strong> Storing references to your recent AI generations for quick access</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">UI Customization:</strong> Remembering your workspace layout and tool configurations</span>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.3. Analytics and Performance Cookies</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              These cookies help us understand how users interact with the Platform:
            </p>
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Usage Analytics:</strong> Tracking which features are most popular (e.g., text vs. image generation)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Performance Monitoring:</strong> Measuring page load times and identifying technical issues</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Error Tracking:</strong> Detecting and diagnosing bugs or crashes</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">A/B Testing:</strong> Comparing different versions of features to improve user experience</span>
              </li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              We use services like Google Analytics to collect this data. All analytics data is aggregated and anonymized 
              to protect your privacy.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.4. Marketing and Advertising Cookies</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              These cookies are used to deliver relevant advertisements and measure campaign effectiveness:
            </p>
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Targeted Advertising:</strong> Showing you ads based on your interests and browsing behavior</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Conversion Tracking:</strong> Measuring whether ads lead to sign-ups or purchases</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Retargeting:</strong> Displaying DAGGPT ads on other websites you visit</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Social Media Integration:</strong> Enabling sharing features and tracking social media referrals</span>
              </li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              You can opt out of marketing cookies through your browser settings or our cookie consent banner.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 text-sm font-black">4</span>
              Third-Party Cookies and Services
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              We work with trusted third-party service providers who may set cookies on your device when you use the Platform.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="px-6 py-4 text-left text-sm font-black uppercase tracking-wider">Service Provider</th>
                    <th className="px-6 py-4 text-left text-sm font-black uppercase tracking-wider">Purpose</th>
                    <th className="px-6 py-4 text-left text-sm font-black uppercase tracking-wider">Cookie Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-slate-900 font-semibold">Google Analytics</td>
                    <td className="px-6 py-4 text-slate-600">Usage analytics and performance monitoring</td>
                    <td className="px-6 py-4 text-slate-600">Analytics</td>
                  </tr>
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-slate-900 font-semibold">Stripe</td>
                    <td className="px-6 py-4 text-slate-600">Payment processing and fraud prevention</td>
                    <td className="px-6 py-4 text-slate-600">Necessary</td>
                  </tr>
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-slate-900 font-semibold">Cloudflare</td>
                    <td className="px-6 py-4 text-slate-600">DDoS protection and content delivery</td>
                    <td className="px-6 py-4 text-slate-600">Necessary</td>
                  </tr>
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-slate-900 font-semibold">Google Vertex AI</td>
                    <td className="px-6 py-4 text-slate-600">AI model processing and generation</td>
                    <td className="px-6 py-4 text-slate-600">Functional</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-slate-600 leading-relaxed">
              Each third-party service has its own privacy policy governing how they use cookies and personal data. 
              We recommend reviewing their policies for more information.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-12 bg-amber-50/50 rounded-2xl p-8 border border-amber-100">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-600 text-white text-sm font-black">5</span>
              Managing Your Cookie Preferences
            </h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">5.1. Cookie Consent Banner</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              When you first visit the Platform, you will see a cookie consent banner allowing you to accept or customize 
              your cookie preferences. You can change your preferences at any time by clicking the "Cookie Settings" link 
              in the footer of the Platform.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">5.2. Browser Settings</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              Most web browsers allow you to control cookies through their settings. You can:
            </p>
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span>Block all cookies</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span>Block third-party cookies only</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span>Delete cookies after each browsing session</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span>Receive notifications when cookies are set</span>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">5.3. Browser-Specific Instructions</h3>
            <ul className="space-y-3 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Google Chrome:</strong> Settings → Privacy and Security → Cookies and other site data
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Mozilla Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Safari:</strong> Preferences → Privacy → Manage Website Data
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Microsoft Edge:</strong> Settings → Cookies and site permissions → Cookies and site data
                </div>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">5.4. Impact of Blocking Cookies</h3>
            <p className="text-slate-600 leading-relaxed">
              Please note that blocking or deleting cookies may impact your experience on the Platform. Some features may 
              not function properly, and you may need to re-enter your preferences each time you visit. Strictly necessary 
              cookies cannot be disabled as they are essential for the Platform to operate.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 text-sm font-black">6</span>
              Data Retention and Cookie Lifespan
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Different cookies have different lifespans:
            </p>
            <ul className="space-y-3 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Session Cookies:</strong> Deleted when you close your browser
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Authentication Cookies:</strong> Typically expire after 30 days of inactivity
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Preference Cookies:</strong> Stored for up to 12 months
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Analytics Cookies:</strong> Typically expire after 24 months
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <div>
                  <strong className="text-slate-900">Marketing Cookies:</strong> Expire after 90 days to 2 years, depending on the provider
                </div>
              </li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              You can manually delete cookies at any time through your browser settings.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 text-sm font-black">7</span>
              International Data Transfers
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Some of our third-party service providers are located outside the United Arab Emirates. When you use the 
              Platform, cookies may transfer data to servers in the United States, European Union, or other jurisdictions.
            </p>
            <p className="text-slate-600 leading-relaxed">
              We ensure that all data transfers comply with applicable data protection laws, including the EU General Data 
              Protection Regulation (GDPR), through the use of Standard Contractual Clauses and other appropriate safeguards.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 text-sm font-black">8</span>
              Children's Privacy
            </h2>
            <p className="text-slate-600 leading-relaxed">
              The Platform is not intended for individuals under the age of 18. We do not knowingly collect information 
              from children through cookies or any other means. If you believe a child has provided us with personal 
              information, please contact us at <a href="mailto:support@daggpt.network" className="text-amber-600 hover:text-amber-700 font-semibold">support@daggpt.network</a>, 
              and we will take steps to delete such information.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 text-sm font-black">9</span>
              Updates to This Cookie Policy
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We may update this Cookie Policy from time to time to reflect changes in our practices, technologies, legal 
              requirements, or other factors. When we make material changes, we will notify you by:
            </p>
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span>Posting a prominent notice on the Platform</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span>Sending an email to the address associated with your account</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 font-bold mt-1">•</span>
                <span>Updating the "Last Updated" date at the top of this policy</span>
              </li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              Your continued use of the Platform after the effective date of the updated Cookie Policy constitutes your 
              acceptance of the changes.
            </p>
          </section>

          {/* Section 10 - Contact */}
          <section className="mb-12 bg-slate-900 text-white rounded-2xl p-8">
            <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white text-slate-900 text-sm font-black">10</span>
              Contact Information
            </h2>
            <div className="space-y-4">
              <div>
                <p className="font-black text-lg mb-2">QHTECH SOLUTIONS L.L.C</p>
                <div className="flex items-start gap-3 text-slate-300">
                  <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                  <p className="leading-relaxed">
                    Meydan Grandstand, 6th floor, Meydan Road,<br />
                    Nad Al Sheba, Dubai, U.A.E.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-amber-400" />
                <a href="mailto:support@daggpt.network" className="text-amber-400 hover:text-amber-300 font-semibold transition-colors">
                  support@daggpt.network
                </a>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-700">
              <p className="text-slate-400 text-sm leading-relaxed">
                If you have questions about our use of cookies or this Cookie Policy, please contact us at the email 
                address above or through our support channels.
              </p>
            </div>
          </section>

        </div>

        {/* Back to Top Button */}
        <div className="mt-16 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-black text-white rounded-xl font-bold transition-all hover:scale-105"
          >
            Back to Top
          </button>
        </div>
      </div>
    </div>
  );
}
